import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BiHistory } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import Breadcrumbs from 'Utils/Breadcrumbs';
import {
  reportSources, reportNoun, IsolationReport, ReportSection, FoldedRows,
} from 'DataGraph/ObjectReport';
import { getRun, errorText } from '../tdmApi';
import { fmtNum, fmtBytes, fmtWhen, unitNoun, RUN_STATUS_TONE, STEP_LABEL } from '../tdmUtils';
import RunStepStrip, { stepLabel } from '../RunStepStrip';
import SectionTitle from '../SectionTitle';
import usePolling from '../usePolling';

// One subset run in full (2026-11-02). Run LISTS carry summaries (no object report, no mask outcome,
// step arrays as counts — a relational source's report alone can be megabytes); this page fetches the
// whole run with GET /api/v1/tdm/runs/{id} and re-reads it while it is PENDING or RUNNING. It only
// informs: nothing here gates a run, a preview or a deploy.

const ACTIVE = ['PENDING', 'RUNNING'];
const LIST_LIMIT = 20;
// Read per render so a test can shorten it.
const pollMs = () => Number(process.env.REACT_APP_TDM_RUN_POLL_MS) || 3000;

/**
 * What the run's source discoveries could not decide (a table the plan reaches that discovery named
 * differently, an entity with no algorithm, …): each DISCOVER step carries them in its detail. Run
 * summaries keep them, so the subset editor's runs table shows them too.
 */
export const DiscoverWarnings = ({ steps }) => {
  const warnings = (steps || []).flatMap((s) => (s.stepType === 'DISCOVER' && s.detail && Array.isArray(s.detail.warnings) ? s.detail.warnings : []));
  if (warnings.length === 0) return null;
  return (
    <ul className='mb-0 pl-3 tdm-muted' data-testid='tdm-run-discover-warnings'>
      {warnings.map((w, i) => <li key={i}>{w}</li>)}
    </ul>
  );
};

const Pill = ({ tone, children, testId }) => <span className={`tdm-pill tdm-pill--${tone || 'neutral'}`} data-testid={testId}>{children}</span>;
const StatusPill = ({ status, testId }) => <Pill tone={RUN_STATUS_TONE[status]} testId={testId}>{status}</Pill>;

// A per-object / per-table verdict: MASKED green, FAILED red, NOT_NEEDED neutral.
const VERDICT_TONE = { MASKED: 'success', FAILED: 'danger', NOT_NEEDED: 'neutral' };
const VerdictPill = ({ verdict }) => (verdict
  ? <Pill tone={VERDICT_TONE[verdict]}>{String(verdict).replace(/_/g, ' ')}</Pill>
  : <span className='tdm-muted'>—</span>);

// The engine a source reads, for the report's nouns (Object / Collection / Table): the connection's,
// else the kind the worker staged it as (JDBC | SALESFORCE | MONGO).
const KIND_ENGINE = { SALESFORCE: 'salesforce', MONGO: 'mongodb' };
const engineOf = (connections, dataSourceId, kind) => {
  const c = dataSourceId == null ? null : (connections || []).find((x) => String(x.id) === String(dataSourceId));
  if (c && (c.type || c.engine)) return c.type || c.engine;
  return KIND_ENGINE[String(kind || '').toUpperCase()] || null;
};

/**
 * `maskOutcome` per source: { slug, tables: [{ name, verdict, reason }], counts } with FAILED tables
 * first, so the ones that matter are on screen when a long list folds. Delphix `chunks` are ignored.
 */
export const maskOutcomeSources = (outcome) => {
  const sources = (outcome && outcome.sources) || {};
  const errors = (outcome && outcome.errors) || {};
  return Object.keys(sources).map((slug) => {
    const verdicts = sources[slug] || {};
    const reasons = errors[slug] || {};
    const tables = Object.keys(verdicts).map((name) => ({ name, verdict: verdicts[name], reason: reasons[name] || null }));
    const counts = { MASKED: 0, NOT_NEEDED: 0, FAILED: 0 };
    tables.forEach((t) => { counts[t.verdict] = (counts[t.verdict] || 0) + 1; });
    return { slug, tables: [...tables.filter((t) => t.verdict === 'FAILED'), ...tables.filter((t) => t.verdict !== 'FAILED')], counts };
  });
};

const Facts = ({ run }) => (
  <dl className='tdm-kv' data-testid='tdm-run-detail-facts'>
    <dt>Started</dt>
    <dd data-testid='tdm-run-detail-when'>{fmtWhen(run.startedAt)}{run.finishedAt ? ` → ${fmtWhen(run.finishedAt)}` : ''}</dd>
    <dt>Run path</dt><dd className='tdm-mono' data-testid='tdm-run-detail-path'>{run.runPath || '—'}</dd>
    <dt>Staging root</dt><dd className='tdm-mono' data-testid='tdm-run-detail-root'>{run.stagingRoot || '—'}</dd>
    {run.budgetExceeded && (
      <>
        <dt>Budget</dt>
        <dd data-testid='tdm-run-detail-budget'>budget hit — admission stopped at a whole-household boundary</dd>
      </>
    )}
    {run.error && (
      <>
        <dt>Error</dt>
        <dd className='tdm-text-danger' data-testid='tdm-run-detail-run-error'>{run.error}</dd>
      </>
    )}
    {(run.provisionBlocker || run.provisionable) && (
      <>
        <dt>Deploy</dt>
        {run.provisionBlocker
          ? <dd className='tdm-text-danger' data-testid='tdm-run-detail-blocker'>cannot be deployed — {run.provisionBlocker}</dd>
          : <dd data-testid='tdm-run-detail-deployable'>ready to deploy</dd>}
      </>
    )}
  </dl>
);

const StepsSection = ({ steps }) => (
  <div className='tdm-section' data-testid='tdm-run-detail-steps'>
    <SectionTitle title='Steps' help='Discover (one per source, in Discover-then-mask subsets) → Stage → Mask (Delphix only). A Discover step opens its discovery job.' />
    {steps.length === 0 ? <div className='tdm-note'>No steps recorded yet.</div> : (
      <>
        <RunStepStrip steps={steps} />
        <div className='tdm-table tdm-table--run-steps mt-2'>
          <div className='tdm-table__head'><span>Step</span><span>Status</span><span>Started</span><span>Finished</span><span>Error</span></div>
          {steps.map((s, i) => (
            <div className='tdm-table__row' key={s.id || i} data-testid={`tdm-run-detail-step-${s.id || i}`}>
              <span>{stepLabel(s)}</span>
              <span><StatusPill status={s.status} /></span>
              <span>{fmtWhen(s.startedAt)}</span>
              <span>{fmtWhen(s.finishedAt)}</span>
              <span className={s.error ? 'tdm-text-danger' : 'tdm-muted'}>{s.error || '—'}</span>
            </div>
          ))}
        </div>
      </>
    )}
    <DiscoverWarnings steps={steps} />
  </div>
);

// One source's staged tables: name, rows, bytes, parts and (inline masking) the per-table verdict.
const StagedObjects = ({ source, noun }) => {
  const objects = Array.isArray(source.perObject) ? source.perObject : [];
  const tid = `tdm-run-detail-objects-${source.slug}`;
  return (
    <ReportSection title={source.slug || 'source'} count={objects.length} testId={tid}>
      {objects.length === 0 ? <div className='dg-muted'>Nothing staged from this source.</div> : (
        <div className='dg-table tdm-run-table--objects' role='table'>
          <div className='dg-table__head' role='row'>
            <span>{noun}</span><span className='dg-num'>Rows</span><span className='dg-num'>Bytes</span><span className='dg-num'>Parts</span><span>Masking</span>
          </div>
          <FoldedRows items={objects} limit={LIST_LIMIT} testId={tid}>
            {(o) => (
              <div className='dg-table__row' role='row' key={o.name} data-testid={`${tid}-${o.name}`}>
                <span className='dg-mono' title={o.file || o.name}>{o.name}</span>
                <span className='dg-num'>{fmtNum(o.rows)}</span>
                <span className='dg-num'>{fmtBytes(o.bytes)}</span>
                <span className='dg-num'>{fmtNum(o.parts)}</span>
                <span><VerdictPill verdict={o.masking} /></span>
              </div>
            )}
          </FoldedRows>
        </div>
      )}
    </ReportSection>
  );
};

const StagedSection = ({ run, connections }) => {
  const t = run.totals;
  const perSource = t && Array.isArray(t.perSource) ? t.perSource : [];
  return (
    <div className='tdm-section' data-testid='tdm-run-detail-staged'>
      <SectionTitle title='Staged' help='What the run wrote to staging: rows and bytes of the CSV as written (after inline masking, before encryption), per source and per table.' />
      {!t ? <div className='tdm-note'>{ACTIVE.includes(run.status) ? 'Nothing staged yet.' : 'This run recorded no totals.'}</div> : (
        <>
          <div className='tdm-run-totals' data-testid='tdm-run-detail-totals'>
            {fmtNum(t.rows)} rows · {fmtBytes(t.bytes)} · {fmtNum(t.files)} file{Number(t.files) === 1 ? '' : 's'}
          </div>
          {perSource.length > 0 && (
            <div className='dg-table tdm-run-table--sources mt-2' role='table' data-testid='tdm-run-detail-sources'>
              <div className='dg-table__head' role='row'>
                <span>Source</span><span>Role</span><span className='dg-num'>Rows</span><span className='dg-num'>Bytes</span><span className='dg-num'>Keys found</span>
              </div>
              {perSource.map((p) => (
                <div className='dg-table__row' role='row' key={p.slug} data-testid={`tdm-run-detail-source-${p.slug}`}>
                  <span className='dg-mono'>{p.slug}</span>
                  <span className='tdm-muted'>{p.role ? String(p.role).toLowerCase() : '—'}</span>
                  <span className='dg-num'>{fmtNum(p.rows)} {unitNoun(p.kind)}</span>
                  <span className='dg-num'>{fmtBytes(p.bytes)}</span>
                  <span className='dg-num'>{p.keysRequested != null ? `${fmtNum(p.keysFound)} of ${fmtNum(p.keysRequested)}` : '—'}</span>
                </div>
              ))}
            </div>
          )}
          {perSource.map((p) => (
            <StagedObjects key={p.slug} source={p} noun={reportNoun(engineOf(connections, p.dataSourceId, p.kind))} />
          ))}
        </>
      )}
    </div>
  );
};

// One source's isolation report, in full (contract 3.0): findings, every object with its rows split rule /
// integrity and each rule's values sent → rows matched (why an object got 0), and what a provision of this run
// will need: the lookups to objects off the list, with the run's counts.
const SourceReport = ({ r, noun }) => {
  const tid = `tdm-run-detail-report-${r.slug || r.dataSourceId}`;
  return (
    <div className='tdm-run-report-source' data-testid={tid}>
      <div className='tdm-run-report-source__head'>
        <strong>{r.dataSourceName || r.slug || `data source #${r.dataSourceId}`}</strong>
        <span className='tdm-muted'>{r.dataGraphName ? ` · data graph ${r.dataGraphName}` : ''}</span>
      </div>
      <IsolationReport report={r} noun={noun} limit={LIST_LIMIT} testId={`${tid}-iso`} />
    </div>
  );
};

const ObjectReportSection = ({ run, connections }) => {
  const sources = reportSources(run.objectReport);
  const perSource = (run.totals && Array.isArray(run.totals.perSource)) ? run.totals.perSource : [];
  const kindOf = (r) => (perSource.find((p) => p.slug === r.slug) || {}).kind;
  return (
    <div className='tdm-section' data-testid='tdm-run-detail-report'>
      <SectionTitle title='Isolation report' help="Per source: what each rule and integrity link reached, what is isolated, and what a provision will need. It informs and never changes the run's status." />
      {sources.length === 0
        ? <div className='tdm-note' data-testid='tdm-run-detail-report-none'>No isolation report for this run (runs before 2026-11-01 have none).</div>
        : sources.map((r) => (
          <SourceReport key={r.slug || r.dataSourceId} r={r} noun={reportNoun(engineOf(connections, r.dataSourceId, kindOf(r)))} />
        ))}
    </div>
  );
};

const MaskOutcomeSection = ({ run }) => {
  const outcome = run.maskOutcome;
  const sources = maskOutcomeSources(outcome);
  const inline = outcome && outcome.maskingJobStatus === 'INLINE';
  return (
    <div className='tdm-section' data-testid='tdm-run-detail-mask'>
      <SectionTitle title='Masking outcome' help='Per staged table: MASKED, NOT NEEDED (no column to mask) or FAILED with the reason. A run is deployable only when no table FAILED.' />
      {!outcome ? <div className='tdm-note' data-testid='tdm-run-detail-mask-none'>No masking outcome recorded{ACTIVE.includes(run.status) ? ' yet' : ''}.</div> : (
        <>
          <div className='tdm-note' data-testid='tdm-run-detail-mask-head'>
            {inline ? 'Masked inline while staging' : `Masking job${outcome.maskingJobId != null ? ` #${outcome.maskingJobId}` : ''}${outcome.maskingJobStatus ? ` · ${outcome.maskingJobStatus}` : ''}`}
            {outcome.recordedAt ? ` · recorded ${fmtWhen(outcome.recordedAt)}` : ''}
          </div>
          {outcome.note && <div className='tdm-note'>{outcome.note}</div>}
          {sources.map((s) => {
            const tid = `tdm-run-detail-mask-${s.slug}`;
            const summary = [['MASKED', 'masked'], ['NOT_NEEDED', 'not needed'], ['FAILED', 'failed']]
              .filter(([k]) => s.counts[k] > 0).map(([k, label]) => `${fmtNum(s.counts[k])} ${label}`).join(' · ');
            return (
              <ReportSection key={s.slug} title={s.slug || 'source'} count={s.tables.length} testId={tid} hint={summary || null}>
                {s.tables.length > 0 && (
                  <div className='dg-table tdm-run-table--mask' role='table'>
                    <div className='dg-table__head' role='row'><span>Table</span><span>Verdict</span><span>Reason</span></div>
                    <FoldedRows items={s.tables} limit={LIST_LIMIT} testId={tid}>
                      {(t) => (
                        <div className='dg-table__row' role='row' key={t.name} data-testid={`${tid}-${t.name}`}>
                          <span className='dg-mono' title={t.name}>{t.name}</span>
                          <span><VerdictPill verdict={t.verdict} /></span>
                          <span className={t.reason ? 'tdm-text-danger' : 'tdm-muted'}>{t.reason || '—'}</span>
                        </div>
                      )}
                    </FoldedRows>
                  </div>
                )}
              </ReportSection>
            );
          })}
        </>
      )}
    </div>
  );
};

const RunDetail = () => {
  const { runId } = useParams();
  const { connections } = useContext(AppContext);
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const loaded = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await getRun(runId);
      if (!alive.current) return;
      loaded.current = true;
      setRun(r);
    } catch (e) {
      // A failed re-read keeps the run on screen; only a run that never loaded shows the error.
      if (alive.current && !loaded.current) setError(errorText(e, `Run #${runId} could not be loaded`));
    }
  }, [runId]);
  // The first read, then every few seconds while the run is PENDING or RUNNING.
  const polling = !error && (!run || ACTIVE.includes(run.status));
  usePolling(load, pollMs(), polling);

  const title = `Run #${runId}`;
  if (error) {
    return (
      <div className='tdm-page tdm-page--narrow tdm-editor'>
        <Breadcrumbs items={[{ label: 'Test Data Landscape', to: '/tdm' }, { label: title }]} />
        <PageHeader icon={BiHistory} eyebrow='Test Data Management / Run' header={title} tooltip='One subset run (export) in full' />
        <div className='tdm-section' data-testid='tdm-run-detail-error'>
          <div className='tdm-text-danger'>{error}</div>
          <div className='tdm-note'><Link to='/tdm' data-testid='tdm-run-detail-back'>Back to the Test Data Landscape</Link></div>
        </div>
      </div>
    );
  }

  const running = Boolean(run && ACTIVE.includes(run.status));
  // The Deploy screen picks the newest deployable export by default; this run is in its picker.
  return (
    <FetchContainer fetchingData={!run} absolute={true}>
      {run && (
        <div className='tdm-page tdm-page--narrow tdm-editor' data-testid='tdm-run-detail'>
          <Breadcrumbs items={[
            { label: 'Test Data Landscape', to: '/tdm' },
            { label: run.subsetName || `Subset #${run.subsetId}`, to: `/tdm/subsets/${run.subsetId}` },
            { label: title },
          ]} />
          <PageHeader
            icon={BiHistory}
            eyebrow='Test Data Management / Run'
            header={title}
            tooltip='One subset run (export) in full'
            meta={<span>
              <Link to={`/tdm/subsets/${run.subsetId}`} data-testid='tdm-run-detail-subset'>{run.subsetName || `Subset #${run.subsetId}`}</Link>
              {' · '}<StatusPill status={run.status} testId='tdm-run-detail-status' />
              {running && run.currentStep ? <span className='tdm-muted'> · {STEP_LABEL[run.currentStep] || run.currentStep}</span> : null}
              {run.cancelRequested ? <span className='tdm-muted'> · cancel requested</span> : null}
            </span>}
            primaryAction={run.provisionable ? (
              <Link className='btn btn-primary btn-sm pl-4 pr-4' to={`/tdm/subsets/${run.subsetId}/provision`}
                data-testid='tdm-run-detail-provision' title={`Open the Deploy screen and pick run #${run.id} there`}>
                Deploy
              </Link>
            ) : null}
          />
          <div className='tdm-section' data-testid='tdm-run-detail-header'>
            <Facts run={run} />
          </div>
          <StepsSection steps={run.steps || []} />
          <StagedSection run={run} connections={connections} />
          <ObjectReportSection run={run} connections={connections} />
          <MaskOutcomeSection run={run} />
        </div>
      )}
    </FetchContainer>
  );
};

export default RunDetail;
